import { recorderDiagnosticsLog, type RecorderDiagnosticsLog, type RecorderLogLevel } from './recorderDiagnosticsLog';
import { scenarioRecorder } from './scenarioRecorder';

/**
 * Recorder robustness across the spec's edge cases (spec §20). Each edge case,
 * when reported: (a) never crashes, (b) produces a user-visible message,
 * (c) writes a diagnostics log entry, (d) records a scenario.json note where the
 * gap is data-relevant. Recording can always be stopped and still produce a
 * (possibly partial, clearly-flagged) archive — see the archive builder.
 */
export type EdgeCaseCode =
  | 'no-model-open'
  | 'revit-closed'
  | 'grpc-dropped'
  | 'model-changed'
  | 'model-cannot-save'
  | 'no-write-permission'
  | 'scenario-too-large'
  | 'elements-deleted'
  | 'elements-no-stable-id'
  | 'revit-api-unavailable'
  | 'ui-without-grpc'
  | 'grpc-without-ui'
  | 'error-in-revit-command'
  | 'zip-build-failure';

export type EdgeCaseSeverity = 'error' | 'warning';

export type EdgeCaseContext = Record<string, string | number | boolean>;

export interface EdgeCaseOutcome {
  code: EdgeCaseCode;
  severity: EdgeCaseSeverity;
  userMessage: string;
  event: string;
  scenarioNote: boolean;
}

interface EdgeCaseSpec {
  severity: EdgeCaseSeverity;
  /** Surface the gap into scenario.json (uncollected-data note). */
  scenarioNote: boolean;
  message: (ctx: EdgeCaseContext) => string;
}

const SPECS: Record<EdgeCaseCode, EdgeCaseSpec> = {
  'no-model-open': {
    severity: 'error',
    scenarioNote: false,
    message: () => 'No Revit model is open. Open a model before starting a recording.',
  },
  'revit-closed': {
    severity: 'error',
    scenarioNote: true,
    message: () => 'Revit closed during recording. The scenario was stopped and saved as a partial archive.',
  },
  'grpc-dropped': {
    severity: 'error',
    scenarioNote: true,
    message: (ctx) =>
      `Connection to Revit dropped${ctx.method ? ` during ${ctx.method}` : ''}. Steps captured up to this point are kept.`,
  },
  'model-changed': {
    severity: 'warning',
    scenarioNote: true,
    message: () => 'The model changed during recording. Captured data may be inconsistent with the final state.',
  },
  'model-cannot-save': {
    severity: 'warning',
    scenarioNote: true,
    message: () => 'The model could not be saved or exported. It was recorded as a reference only.',
  },
  'no-write-permission': {
    severity: 'warning',
    scenarioNote: true,
    message: () => 'No write permission for the model. It was recorded as a reference only.',
  },
  'scenario-too-large': {
    severity: 'warning',
    scenarioNote: true,
    message: (ctx) =>
      `The scenario is large (${ctx.sizeBytes ?? '?'} bytes). Consider Lightweight mode to keep the archive small.`,
  },
  'elements-deleted': {
    severity: 'warning',
    scenarioNote: true,
    message: (ctx) =>
      `${ctx.count ?? 'Some'} element(s) were deleted during recording and could not be captured.`,
  },
  'elements-no-stable-id': {
    severity: 'warning',
    scenarioNote: true,
    message: (ctx) =>
      `${ctx.count ?? 'Some'} element(s) have no stable id and may not replay reliably.`,
  },
  'revit-api-unavailable': {
    severity: 'error',
    scenarioNote: true,
    message: () => 'Revit is not responding. Element and model data could not be collected.',
  },
  'ui-without-grpc': {
    severity: 'warning',
    scenarioNote: false,
    message: (ctx) =>
      `Recorded a UI action${ctx.action ? ` (${ctx.action})` : ''} with no resulting Revit call.`,
  },
  'grpc-without-ui': {
    severity: 'warning',
    scenarioNote: false,
    message: (ctx) =>
      `Recorded a Revit call${ctx.method ? ` (${ctx.method})` : ''} with no preceding UI action.`,
  },
  'error-in-revit-command': {
    severity: 'error',
    scenarioNote: true,
    message: (ctx) =>
      `A Revit command${ctx.command ? ` (${ctx.command})` : ''} failed during recording. The error was captured.`,
  },
  'zip-build-failure': {
    severity: 'error',
    scenarioNote: false,
    message: () => 'Failed to build the scenario archive (zip error). Try recording again.',
  },
};

/** Every known edge-case code (drives the exhaustive guard test). */
export const EDGE_CASE_CODES = Object.keys(SPECS) as EdgeCaseCode[];

function levelFor(severity: EdgeCaseSeverity): RecorderLogLevel {
  return severity === 'error' ? 'ERROR' : 'WARN';
}

export interface RecorderEdgeCaseOptions {
  /** Records a scenario.json note. Defaults to the recorder. */
  warningSink?: (message: string) => void;
  /** Surfaces the outcome to the user (UI). Defaults to no-op. */
  onUserMessage?: (outcome: EdgeCaseOutcome) => void;
}

export class RecorderEdgeCases {
  private readonly log: RecorderDiagnosticsLog;
  private readonly warningSink: (message: string) => void;
  private readonly onUserMessage?: (outcome: EdgeCaseOutcome) => void;

  constructor(log: RecorderDiagnosticsLog, options: RecorderEdgeCaseOptions = {}) {
    this.log = log;
    this.warningSink = options.warningSink ?? ((message) => scenarioRecorder.recordWarning(message));
    this.onUserMessage = options.onUserMessage;
  }

  /**
   * Handle one edge case: log it, note it in scenario.json when applicable, and
   * surface a user message. Never throws.
   */
  report(code: EdgeCaseCode, ctx: EdgeCaseContext = {}): EdgeCaseOutcome {
    const spec = SPECS[code];
    const userMessage = spec.message(ctx);
    const event = `edge.${code}`;

    this.log.log(levelFor(spec.severity), event, ctx);
    if (spec.scenarioNote) {
      this.warningSink(userMessage);
    }

    const outcome: EdgeCaseOutcome = {
      code,
      severity: spec.severity,
      userMessage,
      event,
      scenarioNote: spec.scenarioNote,
    };
    if (this.onUserMessage) {
      this.onUserMessage(outcome);
    }
    return outcome;
  }
}

/** App-wide edge-case handler; warnings flow into the recorder (→ scenario.json). */
export const recorderEdgeCases = new RecorderEdgeCases(recorderDiagnosticsLog);
